import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/contexts/AuthContext';
import { AlertCircle, Eye, EyeOff, Shield, TrendingUp, BarChart3, Lock } from 'lucide-react';

export default function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const data = await login(emailOrUsername, password);
      const perms = data?.user?.permissions || user?.permissions || [];
      const isAdmin = Array.isArray(perms) && perms.some((p: string) => p && p.startsWith('ADMIN_'));
      navigate(isAdmin ? '/admin' : '/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-fintech-900">
      {/* Left Side — Brand */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden items-center justify-center login-grid-bg">
        {/* Background blobs */}
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-primary-600/10 rounded-full blur-[120px] animate-float-blob" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-accent/8 rounded-full blur-[100px] animate-float-blob-delay" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary-800/5 rounded-full blur-[150px] animate-float-blob-slow" />

        {/* Subtle grid lines */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-0 left-1/3 w-px h-full bg-white" />
          <div className="absolute top-0 left-2/3 w-px h-full bg-white" />
          <div className="absolute top-1/3 left-0 h-px w-full bg-white" />
          <div className="absolute top-2/3 left-0 h-px w-full bg-white" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-lg px-12">
          {/* Logo */}
          <div className="animate-login-fade-up animate-login-delay-1 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
                <span className="text-white font-bold text-lg tracking-tight">GR</span>
              </div>
              <span className="text-white font-bold text-xl tracking-tight">GR Trading</span>
            </div>
          </div>

          {/* Headline */}
          <div className="animate-login-fade-up animate-login-delay-2">
            <h1 className="text-white text-4xl xl:text-5xl font-extrabold leading-[1.15] tracking-tight mb-6">
              Trade Smarter.<br />
              <span className="bg-gradient-to-r from-primary-400 to-accent bg-clip-text text-transparent">
                Grow With Confidence.
              </span>
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-md mb-10">
              Manage your investments, track your performance, and stay in control with a secure and simple trading platform.
            </p>
          </div>

          {/* Feature items */}
          <div className="animate-login-fade-up animate-login-delay-3 space-y-4">
            {[
              { icon: Shield, text: 'Secure Platform' },
              { icon: BarChart3, text: 'Real-Time Insights' },
              { icon: TrendingUp, text: 'Simple & Transparent' },
            ].map((feat) => (
              <div key={feat.text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/[0.06] flex items-center justify-center">
                  <feat.icon className="w-4 h-4 text-accent" />
                </div>
                <span className="text-slate-300 text-sm font-medium">{feat.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side — Login Card */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-8 relative">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-600/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden animate-login-fade-up animate-login-delay-1 mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center">
                <span className="text-white font-bold text-base tracking-tight">GR</span>
              </div>
              <span className="text-white font-bold text-lg tracking-tight">GR Trading</span>
            </div>
          </div>

          {/* Login Card */}
          <div className="login-card-glass rounded-[20px] p-8 shadow-2xl shadow-black/20 animate-login-fade-up animate-login-delay-2">
            {/* Header */}
            <div className="mb-7">
              <h2 className="text-white text-[28px] font-bold tracking-tight">Welcome Back</h2>
              <p className="text-slate-400 text-sm mt-1.5">Sign in to your GR Trading account</p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2.5 p-3.5 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400 mb-5 animate-login-fade-in">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email / Username */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email or Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <svg className="w-4.5 h-4.5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    required
                    placeholder="Enter your email or username"
                    className="login-input-dark w-full h-[52px] pl-11 pr-4 rounded-xl text-sm font-normal"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-slate-500" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    className="login-input-dark w-full h-[52px] pl-11 pr-12 rounded-xl text-sm font-normal"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                  </button>
                </div>
              </div>

              {/* Remember / Forgot */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-4 h-4 rounded border border-white/10 bg-white/5 peer-checked:bg-primary-600 peer-checked:border-primary-600 transition-colors flex items-center justify-center">
                      {rememberMe && (
                        <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-slate-400">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-400 hover:text-primary-300 transition-colors font-medium">
                  Forgot Password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-[52px] rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 focus:ring-offset-fintech-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] flex items-center justify-center"
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/[0.06]" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 text-slate-500 bg-fintech-800/92">or</span>
              </div>
            </div>

            {/* Register */}
            <p className="text-center text-sm text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-accent hover:text-accent-light font-semibold transition-colors">
                Register Now
              </Link>
            </p>
          </div>

          {/* Secure badge */}
          <div className="animate-login-fade-up animate-login-delay-4 mt-6 flex items-center justify-center gap-1.5">
            <Lock className="w-3 h-3 text-slate-600" />
            <span className="text-xs text-slate-600 font-medium">Secure access</span>
          </div>
        </div>
      </div>
    </div>
  );
}
